import React, { useState } from 'react';
import {
  FaClinicMedical,
  FaCalendarAlt,
  FaInfoCircle,
  FaListUl,
  FaTable,
} from 'react-icons/fa';
import PropTypes from 'prop-types';
import Button from '../common/Button'; // Importando o componente genérico

function RelatorioAtendimentos({ atendimentos }) {
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'table'
  const itemsPerPage = 20; // Itens por página
  const [currentPage, setCurrentPage] = useState(1);

  // Paginação dos atendimentos
  const paginatedAtendimentos = atendimentos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleViewModeChange = (mode) => setViewMode(mode);
  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <div
      className="bg-white shadow-lg p-8 mb-8 rounded-2xl"
      data-testid="relatorio-atendimentos"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-bold text-gray-800">Relatórios de Atendimentos</h2>
        <div className="grid gap-6">
          <Button
            type="default"
            label={<FaListUl />}
            onClick={() => handleViewModeChange('list')}
            className={`p-2 rounded-full transition-colors duration-300 ${
              viewMode === 'list'
                ? 'text-primary shadow-md bg-blue-600'
                : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
            }`}
            aria-label="Visualização em lista"
            data-testid="list-view-button"
          />
          <Button
            type="default"
            label={<FaTable />}
            onClick={() => handleViewModeChange('table')}
            className={`p-2 rounded-full transition-colors duration-300 ${
              viewMode === 'table'
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
            }`}
            aria-label="Visualização em tabela"
            data-testid="table-view-button"
          />
        </div>
      </div>

      {viewMode === 'list' ? (
        <ul className="grid grid-cols-1 gap-6" data-testid="list-view">
          {paginatedAtendimentos.map((atendimento) => {
            const dataAtendimento = atendimento.dataAtendimento?.toDate();
            const dataFormatada = dataAtendimento
              ? dataAtendimento.toLocaleDateString()
              : 'Data não disponível';

            return (
              <li
                key={atendimento.id}
                className="border border-gray-200 p-5 rounded-xl bg-white hover:bg-blue-50 transition-colors duration-300 shadow-md hover:shadow-lg"
                data-testid={`atendimento-item-${atendimento.id}`}
              >
                <h4 className="text-lg font-bold text-gray-800 mb-2">
                  {atendimento.nomePaciente}
                </h4>
                <p className="text-gray-700 text-sm flex items-center mb-1">
                  <FaClinicMedical className="mr-2 text-blue-500" />
                  {atendimento.clinica}
                </p>
                <p className="text-gray-700 text-sm flex items-center mb-1">
                  <FaCalendarAlt className="mr-2 text-green-500" />
                  {dataFormatada}
                </p>
                <p className="text-gray-700 text-sm flex items-center">
                  <FaInfoCircle className="mr-2 text-orange-500" />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: atendimento.observacao || 'Sem observação',
                    }}
                  />
                </p>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="min-w-full bg-white border border-gray-200 rounded-xl shadow-md"
            data-testid="table-view"
          >
            <thead className="bg-blue-100">
              <tr>
                <th className="py-3 px-4 text-left text-gray-700 font-bold">
                  Paciente
                </th>
                <th className="py-3 px-4 text-left text-gray-700 font-bold">
                  Clínica
                </th>
                <th className="py-3 px-4 text-left text-gray-700 font-bold">
                  Data
                </th>
                <th className="py-3 px-4 text-left text-gray-700 font-bold">
                  Observação
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedAtendimentos.map((atendimento) => {
                const dataAtendimento = atendimento.dataAtendimento?.toDate();
                const dataFormatada = dataAtendimento
                  ? dataAtendimento.toLocaleDateString()
                  : 'Data não disponível';

                return (
                  <tr
                    key={atendimento.id}
                    className="border-b hover:bg-blue-50 transition-colors duration-200"
                    data-testid={`table-atendimento-item-${atendimento.id}`}
                  >
                    <td className="py-3 px-4 text-gray-800 text-sm font-medium">
                      {atendimento.nomePaciente}
                    </td>
                    <td className="py-3 px-4 text-gray-800 text-sm font-medium">
                      {atendimento.clinica}
                    </td>
                    <td className="py-3 px-4 text-gray-800 text-sm font-medium">
                      {dataFormatada}
                    </td>
                    <td
                      className="py-3 px-4 text-gray-800 text-sm font-medium"
                      dangerouslySetInnerHTML={{
                        __html: atendimento.observacao || 'Sem observação',
                      }}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginação */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0 sm:space-x-4">
        <Button
          type="default"
          label="Anterior"
          onClick={() => handlePageChange(currentPage - 1)}
          className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400 transition-colors duration-300"
          disabled={currentPage === 1}
          data-testid="previous-page-button"
        />
        <p
          className="text-gray-800 font-medium text-center sm:text-left"
          data-testid="pagination-info"
        >
          Página {currentPage} de{' '}
          {Math.ceil(atendimentos.length / itemsPerPage)}
        </p>
        <Button
          type="default"
          label="Próximo"
          onClick={() => handlePageChange(currentPage + 1)}
          className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400 transition-colors duration-300"
          disabled={
            currentPage === Math.ceil(atendimentos.length / itemsPerPage)
          }
          data-testid="next-page-button"
        />
      </div>
    </div>
  );
}

// Definindo os propTypes para garantir que os dados são passados corretamente
RelatorioAtendimentos.propTypes = {
  atendimentos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nomePaciente: PropTypes.string,
      clinica: PropTypes.string,
      dataAtendimento: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.object,
      ]),
      observacao: PropTypes.string,
    })
  ).isRequired,
};

export default RelatorioAtendimentos;
